<script setup lang="ts">
  import { computed, onMounted, ref, watch } from 'vue';

  const props = withDefaults(
    defineProps<{
      modelValue: number;
      min: number;
      max: number;
      label?: string;
      suffix?: string;
      inputPlaceholder?: string;
      inputWidthClass?: string;
      emptyFallback?: number;
    }>(),
    {
      label: '',
      suffix: '',
      inputPlaceholder: '',
      inputWidthClass: 'w-[90px]',
      emptyFallback: undefined,
    }
  );

  const emit = defineEmits<{ (e: 'update:modelValue', value: number): void }>();

  const rangeEl = ref<HTMLInputElement | null>(null);
  const trackWidth = ref(192);
  const inputText = ref(String(props.modelValue));

  watch(
    () => props.modelValue,
    (v) => {
      inputText.value = String(v);
    }
  );

  onMounted(() => {
    if (rangeEl.value) trackWidth.value = rangeEl.value.offsetWidth;
  });

  function thumbCorrectedFill(value: number, min: number, max: number, width: number): string {
    const raw = (value - min) / (max - min);
    const corrected = ((raw * (width - 16) + 8) / width) * 100;
    return `${corrected}%`;
  }

  const progress = computed(() =>
    thumbCorrectedFill(props.modelValue, props.min, props.max, trackWidth.value)
  );

  function clamp(value: number): number {
    return Math.min(props.max, Math.max(props.min, value));
  }

  function onTextInput(e: Event): void {
    inputText.value = (e.target as HTMLInputElement).value.replace(/\D/g, '').slice(0, 3);
  }

  function commitTextValue(): void {
    const parsed = Number.parseInt(inputText.value, 10);
    if (Number.isNaN(parsed) || inputText.value === '') {
      const fallback = props.emptyFallback ?? props.max;
      emit('update:modelValue', fallback);
      inputText.value = String(fallback);
    } else {
      const clamped = clamp(parsed);
      emit('update:modelValue', clamped);
      inputText.value = String(clamped);
    }
  }

  function onRangeInput(e: Event): void {
    const num = Number((e.target as HTMLInputElement).value);
    if (!Number.isNaN(num)) emit('update:modelValue', clamp(num));
  }
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex items-start justify-between">
      <p
        v-if="label"
        class="text-sm text-[var(--color-gray-07)] leading-5"
        v-html="label"
      />
      <div
        :class="[
          'bg-white-01 border border-[var(--color-gray-02)] flex h-[42px] items-center justify-between overflow-hidden pl-4 pr-3 py-2.5 rounded-lg shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]',
          inputWidthClass,
        ]"
      >
        <input
          type="text"
          inputmode="numeric"
          maxlength="3"
          :min="min"
          :max="max"
          :placeholder="inputPlaceholder"
          :value="inputText"
          class="border-0 outline-none p-0 w-full text-sm text-[var(--color-gray-07)] bg-transparent"
          @focus="($event.target as HTMLInputElement).select()"
          @input="onTextInput"
          @blur="commitTextValue"
          @keydown.enter="commitTextValue"
        >
        <span
          v-if="suffix"
          class="text-[var(--color-gray-03)] text-sm shrink-0"
        >{{ suffix }}</span>
      </div>
    </div>
    <input
      ref="rangeEl"
      type="range"
      :min="min"
      :max="max"
      :value="modelValue"
      :style="{ '--val': progress }"
      class="slider-track w-full"
      @input="onRangeInput"
    >
  </div>
</template>

<style scoped>
  .slider-track {
    -webkit-appearance: none;
    appearance: none;
    height: 4px;
    background: linear-gradient(
      to right,
      var(--theme-secondary) var(--val, 0%),
      var(--color-gray-02) var(--val, 0%)
    );
    border-radius: 2px;
    outline: none;
    cursor: pointer;
  }

  .slider-track::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 16px;
    height: 16px;
    background: white;
    border-radius: 8px;
    box-shadow: 0px 2px 5px 0px rgba(0, 0, 0, 0.25);
    cursor: pointer;
  }

  .slider-track::-moz-range-thumb {
    width: 16px;
    height: 16px;
    background: white;
    border-radius: 8px;
    box-shadow: 0px 2px 5px 0px rgba(0, 0, 0, 0.25);
    cursor: pointer;
    border: none;
  }
</style>
